import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import DashboardScheduleSummary from "@/components/schedule/DashboardScheduleSummary";
import TeamAttendanceOverview from "@/components/schedule/TeamAttendanceOverview";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Building2, Wallet, TrendingUp, AlertTriangle, ListChecks, ShieldAlert, Gauge, Cake, CalendarClock, Phone, Handshake, CheckCircle2, BellRing, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HEALTH_STYLES = {
  Healthy: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  Watch: "bg-[#FEF9C3] text-[#A16207] border-[#FEF08A]",
  "At Risk": "bg-red-50 text-red-700 border-red-200",
};

const UTILIZATION_STYLES = {
  Balanced: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  Busy: "bg-[#FEF9C3] text-[#A16207] border-[#FEF08A]",
  Overloaded: "bg-red-50 text-red-700 border-red-200",
};

function KpiCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#E2E8F0] rounded-lg p-5 flex items-start justify-between"
    >
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-[#4B5563] font-semibold mb-2">{label}</p>
        <p className="font-heading text-2xl font-bold text-[#1C1C1E]">{value}</p>
      </div>
      <div className={`h-10 w-10 rounded-md flex items-center justify-center ${accent || "bg-[#F8F9FA]"}`}>
        <Icon className="h-5 w-5 text-[#00823B]" />
      </div>
    </div>
  );
}

const SEVERITY_DOT = { overdue: "bg-red-500", urgent: "bg-amber-500", upcoming: "bg-[#76C121]" };

export default function DashboardHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clientHealth, setClientHealth] = useState([]);
  const [capacity, setCapacity] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [schedule, setSchedule] = useState(null);

  const loadSchedule = () => api.get("/dashboard/today-schedule").then((r) => setSchedule(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/notifications").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data.slice(0, 6))).catch(() => {});
    api.get("/employees/birthdays/upcoming").then((r) => setBirthdays(r.data)).catch(() => {});
    loadSchedule();
    if (user?.role === "admin") {
      api.get("/dashboard/client-health").then((r) => setClientHealth(r.data)).catch(() => {});
      api.get("/dashboard/capacity").then((r) => setCapacity(r.data)).catch(() => {});
    }
  }, [user]);

  const currency = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

  const [reminderTitle, setReminderTitle] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [addingReminder, setAddingReminder] = useState(false);

  const addReminder = async () => {
    if (!reminderTitle.trim()) return;
    setAddingReminder(true);
    try {
      await api.post("/reminders", { title: reminderTitle.trim(), date: new Date().toISOString().slice(0, 10), time: reminderTime || null });
      setReminderTitle("");
      setReminderTime("");
      loadSchedule();
    } catch (e) {
      toast.error("Could not add reminder");
    } finally {
      setAddingReminder(false);
    }
  };

  const completeReminder = async (id) => {
    try {
      await api.delete(`/reminders/${id}`);
      loadSchedule();
    } catch (e) {
      toast.error("Could not update reminder");
    }
  };

  return (
    <DashboardLayout title="Dashboard Home">
      <div className="mb-6">
        <h2 className="font-heading text-xl font-semibold text-[#1C1C1E]">
          Welcome back, {user?.name?.split(" ")[0]}
        </h2>
        <p className="text-sm text-[#4B5563] mt-1">Here's what's happening across EnviroWealth today.</p>
      </div>

      <DashboardScheduleSummary />

      <TodaysSchedule schedule={schedule} navigate={navigate} reminderTitle={reminderTitle} setReminderTitle={setReminderTitle}
        reminderTime={reminderTime} setReminderTime={setReminderTime} addingReminder={addingReminder} addReminder={addReminder} completeReminder={completeReminder} />

      {summary?.role === "admin" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-kpi-grid">
          <KpiCard icon={Users} label="Employees" value={summary.total_employees} testId="kpi-employees" />
          <KpiCard icon={Building2} label="Active Clients" value={summary.active_clients} testId="kpi-active-clients" />
          <KpiCard icon={Wallet} label="Pending Invoices" value={currency(summary.pending_invoice_amount)} testId="kpi-pending-invoices" accent="bg-[#F7FEE7]" />
          <KpiCard icon={TrendingUp} label="Revenue Collected" value={currency(summary.revenue_collected)} testId="kpi-revenue-collected" accent="bg-[#ECFDF5]" />
        </div>
      )}

      {summary?.role === "admin" && <TeamAttendanceOverview />}

      {summary?.role === "employee" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="employee-kpi-grid">
          <KpiCard icon={ListChecks} label="To Do" value={summary.tasks_by_status?.["To Do"] ?? 0} testId="kpi-todo" />
          <KpiCard icon={ListChecks} label="In Progress" value={summary.tasks_by_status?.["In Progress"] ?? 0} testId="kpi-in-progress" />
          <KpiCard icon={ListChecks} label="In Review" value={summary.tasks_by_status?.["Review"] ?? 0} testId="kpi-review" />
          <KpiCard icon={Wallet} label="Total Earned (Paid)" value={currency(summary.total_earned)} testId="kpi-total-earned" accent="bg-[#ECFDF5]" />
        </div>
      )}

      {summary?.role === "admin" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-[#E2E8F0] rounded-lg" data-testid="client-health-panel">
            <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-[#00823B]" />
              <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Client Health &amp; Renewal Risk</h3>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {clientHealth.length === 0 ? (
                <p className="text-sm text-[#4B5563] px-5 py-6 text-center">No active clients to assess.</p>
              ) : (
                clientHealth.map((c) => (
                  <div key={c.client_id} data-testid={`client-health-row-${c.client_id}`} className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] last:border-b-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1C1C1E] truncate">{c.company_name}</p>
                      <p className="text-xs text-[#4B5563]">
                        {c.task_completion_rate}% tasks done
                        {c.last_meeting_days_ago != null && ` · last meeting ${c.last_meeting_days_ago}d ago`}
                        {c.renewal_in_days != null && c.renewal_in_days <= 30 && ` · renewal in ${c.renewal_in_days}d`}
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-md border shrink-0 ${HEALTH_STYLES[c.health]}`}>{c.health}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg" data-testid="team-capacity-panel">
            <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
              <Gauge className="h-4 w-4 text-[#00823B]" />
              <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Team Capacity &amp; Workload</h3>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {capacity.length === 0 ? (
                <p className="text-sm text-[#4B5563] px-5 py-6 text-center">No employees found.</p>
              ) : (
                capacity.map((c) => (
                  <div key={c.employee_id} data-testid={`capacity-row-${c.employee_id}`} className="px-5 py-3 border-b border-[#E2E8F0] last:border-b-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-medium text-[#1C1C1E]">{c.name}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${UTILIZATION_STYLES[c.utilization]}`}>{c.utilization}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#F8F9FA] overflow-hidden">
                      <div
                        className={`h-full rounded-full ${c.utilization === "Overloaded" ? "bg-red-500" : c.utilization === "Busy" ? "bg-amber-500" : "bg-[#76C121]"}`}
                        style={{ width: `${Math.min(c.active_tasks * 15, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-[#4B5563] mt-1">{c.active_tasks} active tasks · {c.high_priority_tasks} high priority · {c.overdue_tasks} overdue</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-lg">
          <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Recent Tasks</h3>
            <button data-testid="dashboard-view-all-tasks" onClick={() => navigate("/tasks")} className="text-xs text-[#00823B] hover:underline font-medium">
              View all
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0]">
                <th className="px-5 py-2 font-medium">Task</th>
                <th className="px-5 py-2 font-medium">Priority</th>
                <th className="px-5 py-2 font-medium">Deadline</th>
                <th className="px-5 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-6 text-center text-[#4B5563]">No tasks yet.</td></tr>
              )}
              {tasks.map((t) => (
                <tr key={t.id} data-testid={`dashboard-task-row-${t.id}`} className="border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <td className="px-5 py-2.5 text-[#1C1C1E] font-medium truncate max-w-[220px]">{t.title}</td>
                  <td className="px-5 py-2.5"><StatusBadge value={t.priority} /></td>
                  <td className="px-5 py-2.5 text-[#4B5563]">{t.deadline || "—"}</td>
                  <td className="px-5 py-2.5"><StatusBadge value={t.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg" data-testid="dashboard-alerts-panel">
          <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#A16207]" />
            <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Upcoming Alerts</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="text-sm text-[#4B5563] px-5 py-6 text-center">No urgent alerts right now.</p>
            ) : (
              alerts.map((a, idx) => (
                <div key={idx} data-testid={`dashboard-alert-${idx}`} className="px-5 py-3 border-b border-[#E2E8F0] last:border-b-0 flex items-start gap-2.5">
                  <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${SEVERITY_DOT[a.severity]}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1C1E] truncate">{a.title}</p>
                    <p className="text-xs text-[#4B5563]">{a.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {birthdays.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg mt-6" data-testid="birthdays-panel">
          <div className="px-5 py-3.5 border-b border-[#E2E8F0] flex items-center gap-2">
            <Cake className="h-4 w-4 text-[#00823B]" />
            <h3 className="font-heading text-sm font-semibold text-[#1C1C1E]">Upcoming Birthdays</h3>
          </div>
          <div className="flex flex-wrap gap-3 p-4">
            {birthdays.map((b) => (
              <div
                key={b.employee_id}
                data-testid={`birthday-row-${b.employee_id}`}
                className="flex items-center gap-2.5 bg-[#F7FEE7] border border-[#76C121]/30 rounded-md px-3.5 py-2.5"
              >
                <div className="h-8 w-8 rounded-full bg-[#76C121]/15 flex items-center justify-center shrink-0">
                  <Cake className="h-4 w-4 text-[#00823B]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1C1C1E]">{b.name}</p>
                  <p className="text-xs text-[#4B5563]">
                    {b.dob_display} · {b.days_away === 0 ? "Today!" : `in ${b.days_away}d`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}


function ScheduleColumn({ icon: Icon, title, items, emptyText, testId, renderItem }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg flex flex-col" data-testid={testId}>
      <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#00823B]" />
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#1C1C1E]">{title}</h4>
        <span className="ml-auto text-xs text-[#4B5563]">{items.length}</span>
      </div>
      <div className="p-2.5 space-y-2 max-h-56 overflow-y-auto flex-1">
        {items.length === 0 ? (
          <p className="text-xs text-[#4B5563] text-center py-4">{emptyText}</p>
        ) : (
          items.map((item, i) => renderItem(item, i))
        )}
      </div>
    </div>
  );
}

function TodaysSchedule({ schedule, navigate, reminderTitle, setReminderTitle, reminderTime, setReminderTime, addingReminder, addReminder, completeReminder }) {
  if (!schedule) return null;
  const timeLabel = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="mb-8" data-testid="todays-schedule-widget">
      <h3 className="font-heading text-sm font-semibold text-[#1C1C1E] mb-3 flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-[#00823B]" /> Today's Schedule
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <ScheduleColumn
          icon={CalendarClock} title="Meetings" items={schedule.meetings} emptyText="No meetings today." testId="schedule-meetings-column"
          renderItem={(m, i) => (
            <div key={i} data-testid={`schedule-meeting-${m.id}`} onClick={() => navigate(m.link)} className="text-xs bg-[#F8F9FA] rounded-md p-2 cursor-pointer hover:bg-[#F7FEE7] transition-colors duration-200">
              <p className="font-medium text-[#1C1C1E] truncate">{m.title}</p>
              <p className="text-[#4B5563]">{timeLabel(m.time)}{m.client_name ? ` · ${m.client_name}` : m.organization ? ` · ${m.organization}` : ""}</p>
            </div>
          )}
        />
        <ScheduleColumn
          icon={Phone} title="Scheduled Calls" items={schedule.calls} emptyText="No calls today." testId="schedule-calls-column"
          renderItem={(c, i) => (
            <div key={i} data-testid={`schedule-call-${c.id}`} onClick={() => navigate(c.link)} className="text-xs bg-[#F8F9FA] rounded-md p-2 cursor-pointer hover:bg-[#F7FEE7] transition-colors duration-200">
              <p className="font-medium text-[#1C1C1E] truncate">{c.title}</p>
              <p className="text-[#4B5563]">{timeLabel(c.time)}{c.client_name ? ` · ${c.client_name}` : ""}</p>
            </div>
          )}
        />
        <ScheduleColumn
          icon={ListChecks} title="Task Deadlines" items={schedule.task_deadlines} emptyText="No deadlines today." testId="schedule-deadlines-column"
          renderItem={(t, i) => (
            <div key={i} data-testid={`schedule-deadline-${t.id}`} onClick={() => navigate(t.link)} className="text-xs bg-[#F8F9FA] rounded-md p-2 cursor-pointer hover:bg-[#F7FEE7] transition-colors duration-200 flex items-center justify-between gap-1.5">
              <span className="font-medium text-[#1C1C1E] truncate">{t.title}</span>
              <StatusBadge value={t.priority} />
            </div>
          )}
        />
        <ScheduleColumn
          icon={Handshake} title="Follow-ups" items={schedule.follow_ups} emptyText="No follow-ups today." testId="schedule-followups-column"
          renderItem={(f, i) => (
            <div key={i} data-testid={`schedule-followup-${f.id}`} onClick={() => navigate(f.link)} className="text-xs bg-[#F8F9FA] rounded-md p-2 cursor-pointer hover:bg-[#F7FEE7] transition-colors duration-200">
              <p className="font-medium text-[#1C1C1E] truncate">{f.title}</p>
              <p className="text-[#4B5563]">{f.stage}</p>
            </div>
          )}
        />
        <div className="bg-white border border-[#E2E8F0] rounded-lg flex flex-col" data-testid="schedule-reminders-column">
          <div className="px-4 py-3 border-b border-[#E2E8F0] flex items-center gap-2">
            <BellRing className="h-4 w-4 text-[#00823B]" />
            <h4 className="text-xs font-semibold uppercase tracking-wide text-[#1C1C1E]">Reminders</h4>
            <span className="ml-auto text-xs text-[#4B5563]">{schedule.reminders.length}</span>
          </div>
          <div className="p-2.5 space-y-2 max-h-40 overflow-y-auto flex-1">
            {schedule.reminders.length === 0 ? (
              <p className="text-xs text-[#4B5563] text-center py-4">No reminders today.</p>
            ) : (
              schedule.reminders.map((r) => (
                <div key={r.id} data-testid={`schedule-reminder-${r.id}`} className="text-xs bg-[#F8F9FA] rounded-md p-2 flex items-center justify-between gap-1.5">
                  <div className="min-w-0">
                    <p className="font-medium text-[#1C1C1E] truncate">{r.title}</p>
                    {r.time && <p className="text-[#4B5563]">{r.time}</p>}
                  </div>
                  <button data-testid={`schedule-reminder-done-${r.id}`} onClick={() => completeReminder(r.id)} title="Mark done" className="text-[#00823B] shrink-0">
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="p-2 border-t border-[#E2E8F0] flex gap-1.5">
            <Input data-testid="schedule-reminder-input" value={reminderTitle} onChange={(e) => setReminderTitle(e.target.value)} placeholder="Quick reminder..." className="h-8 text-xs" />
            <Button size="icon" className="h-8 w-8 shrink-0 bg-[#76C121] hover:bg-[#5C9819] text-white" disabled={addingReminder} onClick={addReminder} data-testid="schedule-reminder-add-button">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
